import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiUpload, useAuth } from '@shop/api-client'
import type { Brand, Category, Product, TaxGroup, Variant } from '../api/types'
import { Perm } from '../api/types'
import { ImageField } from '../ui/ImageField'
import {
  Banner,
  Button,
  ErrorBanner,
  Field,
  Select,
  TextArea,
  TextInput,
} from '@shop/ui'

/**
 * تعديلُ الصنف بكامل تفاصيله — <b>وكلُّه كان يصل ولا يُعرض</b>.
 *
 * الخادمُ يُرسل في <c>ProductSummary</c> ستّةَ عشر حقلًا ويقبل في
 * <c>PATCH /products/{id}</c> عشرةً منها، وكانت الشاشةُ تعرض خمسةً ولا تعدّل
 * إلا السعر. فما بُني هنا واجهةٌ فوق خلفيّةٍ قائمة، لا خلفيّةٌ جديدة.
 *
 * <b>والحقلُ الغائبُ لا يُمسّ</b> — هذا عقدُ <c>PATCH</c> في هذا النظام. ولذلك
 * تُرسل الحقولُ المُفرَّغةُ نصًّا فارغًا لا <c>null</c>: <c>null</c> تعني «لا
 * تلمسه»، والنصُّ الفارغ يعني «امحُه». ولولا التفريق لَما أمكن محوُ طرازٍ كُتب
 * خطأً.
 */
export function ProductEditor({ product, onDone }: { product: Product; onDone: () => void }) {
  const { can } = useAuth()

  const [nameAr, setNameAr] = useState(product.nameAr)
  const [nameEn, setNameEn] = useState(product.nameEn ?? '')
  const [brandId, setBrandId] = useState(product.brandId ?? '')
  const [model, setModel] = useState(product.model ?? '')
  const [categoryId, setCategoryId] = useState(product.categoryId ?? '')
  const [taxGroupId, setTaxGroupId] = useState(product.taxGroupId ?? '')
  const [description, setDescription] = useState(product.description ?? '')
  const [warrantyMonths, setWarrantyMonths] = useState(
    product.warrantyMonths === null ? '' : String(product.warrantyMonths),
  )
  const [warrantyNote, setWarrantyNote] = useState(product.warrantyNote ?? '')
  const [error, setError] = useState<unknown>(null)

  const brands = useQuery({
    queryKey: ['brands'],
    queryFn: () => api<Brand[]>('/api/v1/catalog/brands'),
    enabled: can(Perm.productView),
  })

  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => api<Category[]>('/api/v1/catalog/categories'),
    enabled: can(Perm.productView),
  })

  const taxGroups = useQuery({
    queryKey: ['tax-groups'],
    queryFn: () => api<TaxGroup[]>('/api/v1/catalog/tax-groups'),
    enabled: can(Perm.productView),
  })

  const months = warrantyMonths.trim() === '' ? null : Number(warrantyMonths.trim())
  const monthsBad = months !== null && (!Number.isInteger(months) || months < 0 || months > 600)

  const save = useMutation({
    mutationFn: () =>
      api<Product>(`/api/v1/products/${product.id}`, {
        method: 'PATCH',
        body: {
          nameAr: nameAr.trim(),
          nameEn: nameEn.trim(),
          brandId: brandId === '' ? null : brandId,
          model: model.trim(),
          categoryId: categoryId === '' ? null : categoryId,
          taxGroupId: taxGroupId === '' ? null : taxGroupId,
          description: description.trim(),
          warrantyMonths: months,
          warrantyNote: warrantyNote.trim(),
        },
      }),
    onSuccess: () => {
      setError(null)
      onDone()
    },
    onError: setError,
  })

  const busy = save.isPending

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="الاسم بالعربيّة">
          <TextInput value={nameAr} onChange={setNameAr} disabled={busy} />
        </Field>

        <Field label="الاسم بالإنجليزيّة">
          <TextInput value={nameEn} onChange={setNameEn} dir="ltr" disabled={busy} />
        </Field>

        <Field label="الماركة" hint="تُنشأ الماركاتُ من الإدخال الذكيّ حين تُملى.">
          <Select value={brandId} onChange={setBrandId} disabled={busy}>
            <option value="">بلا ماركة</option>
            {(brands.data ?? []).filter((b) => b.isActive || b.id === brandId).map((brand) => (
              <option key={brand.id} value={brand.id}>{brand.nameAr}</option>
            ))}
          </Select>
        </Field>

        <Field label="الطراز" hint="مع الماركة يصيران قاعدةَ كشف التكرار.">
          <TextInput value={model} onChange={setModel} dir="ltr" disabled={busy} />
        </Field>

        {/* ⚠ **وتعديلُ القسم لاحقًا مقصودٌ ومسموح** — بصلاحيّة
            `catalog.product.edit`، ويُكتب في سجلّ التدقيق كأيّ حقلٍ آخر:
            القيمةُ قبلَه وبعدَه ومن غيّرها ومتى. */}
        <Field label="القسم" hint="عائلةُ المادّة — وتَرِثُها متغيّراتُها كلُّها.">
          <Select value={categoryId} onChange={setCategoryId} disabled={busy}>
            <option value="">(بلا قسم — يُستحسن اختياره)</option>
            {(categories.data ?? []).filter((c) => c.isActive || c.id === categoryId).map((category) => (
              <option key={category.id} value={category.id}>{category.nameAr}</option>
            ))}
          </Select>
        </Field>

        <Field label="المجموعة الضريبيّة">
          <Select value={taxGroupId} onChange={setTaxGroupId} disabled={busy}>
            <option value="">بلا مجموعة</option>
            {(taxGroups.data ?? []).map((group) => (
              <option key={group.id} value={group.id}>
                {group.nameAr} · {group.rate}%
              </option>
            ))}
          </Select>
        </Field>

        <Field label="الكفالة بالأشهر">
          <TextInput value={warrantyMonths} onChange={setWarrantyMonths} dir="ltr" disabled={busy} />
        </Field>

        <Field label="ملاحظةُ الكفالة">
          <TextInput value={warrantyNote} onChange={setWarrantyNote} disabled={busy} />
        </Field>
      </div>

      <Field label="الوصف">
        <TextArea value={description} onChange={setDescription} rows={3} disabled={busy} />
      </Field>

      {monthsBad && <Banner tone="warn">الكفالةُ عددُ أشهرٍ صحيحٌ بين صفرٍ وستّمئة.</Banner>}

      <ErrorBanner error={error} />

      <div className="flex gap-2">
        <Button
          tone="primary"
          disabled={busy || nameAr.trim() === '' || monthsBad}
          onClick={() => save.mutate()}
        >
          {busy ? 'يُحفظ…' : 'احفظ التفاصيل'}
        </Button>
        <Button onClick={onDone} disabled={busy}>إلغاء</Button>
      </div>
    </div>
  )
}

/** صورةُ الصنف — رفعٌ ونزع، وكلاهما بصلاحيّة تعديل الصنف. */
export function ProductImage({ product }: { product: Product }) {
  const queries = useQueryClient()
  const { can } = useAuth()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  // ⚠ والمسارُ يُعلَّق على `hasImage`: بلا ذلك تُطلب صورةُ صنفٍ لا صورةَ له في
  //   كلّ فتح، فيردّ الخادمُ 404 ويمتلئ السجلُّ بأخطاءَ ليست أخطاء.
  const path = product.hasImage ? `/api/v1/products/${product.id}/image` : null

  const refresh = () => queries.invalidateQueries({ queryKey: ['product', product.id] })

  const run = async (work: () => Promise<void>) => {
    setBusy(true)
    setError(null)

    try {
      await work()
      await refresh()
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-2">
      <ImageField
        path={path}
        busy={busy}
        readOnly={!can(Perm.productEdit)}
        onPick={(file) => void run(() => apiUpload(`/api/v1/products/${product.id}/image`, file))}
        onRemove={
          product.hasImage
            ? () => void run(() => api(`/api/v1/products/${product.id}/image`, { method: 'DELETE' }))
            : undefined
        }
      />

      <ErrorBanner error={error} />
    </div>
  )
}

/**
 * تعديلُ المتغيّر: ما يميّزه عن إخوته، وما يُطلب عند بيعه.
 *
 * <b>ولا سعرَ هنا</b> — للسعر محرّرُه وصلاحيّتُه المستقلّة، وهذا الفصلُ مقصودٌ
 * في الخادم (<c>catalog.product.price.edit</c>).
 */
export function VariantEditor({ variant, onDone }: { variant: Variant; onDone: () => void }) {
  const [variantName, setVariantName] = useState(variant.variantName ?? '')
  const [modelNumber, setModelNumber] = useState(variant.modelNumber ?? '')
  const [colour, setColour] = useState(variant.colour ?? '')
  const [capacity, setCapacity] = useState(variant.capacity ?? '')
  const [requiresSerial, setRequiresSerial] = useState(variant.requiresSerial)
  const [requiresImei, setRequiresImei] = useState(variant.requiresImei)
  const [isDefault, setIsDefault] = useState(variant.isDefault)
  const [error, setError] = useState<unknown>(null)

  const save = useMutation({
    mutationFn: () =>
      api<Variant>(`/api/v1/variants/${variant.id}`, {
        method: 'PATCH',
        body: {
          variantName: variantName.trim(),
          modelNumber: modelNumber.trim(),
          colour: colour.trim(),
          capacity: capacity.trim(),
          requiresSerial,

          // والـIMEI يستلزم التسلسل: جهازٌ يُطلب رقمُ IMEI له ولا يُطلب تسلسلُه
          // حالةٌ لا معنى لها — وهي القاعدةُ نفسُها التي يطبّقها الاعتماد.
          requiresImei,
          isDefault,
        },
      }),
    onSuccess: () => {
      setError(null)
      onDone()
    },
    onError: setError,
  })

  const busy = save.isPending

  return (
    <div className="mt-3 space-y-3 rounded-md border border-line bg-surface p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="اسمُ المتغيّر" hint="يُعرض حين لا يكفي اللونُ والسعة.">
          <TextInput value={variantName} onChange={setVariantName} disabled={busy} />
        </Field>

        <Field label="رقمُ الصانع">
          <TextInput value={modelNumber} onChange={setModelNumber} dir="ltr" disabled={busy} />
        </Field>

        <Field label="اللون">
          <TextInput value={colour} onChange={setColour} disabled={busy} />
        </Field>

        <Field label="السعة">
          <TextInput value={capacity} onChange={setCapacity} disabled={busy} />
        </Field>
      </div>

      <div className="space-y-2">
        <Toggle
          checked={requiresImei}
          disabled={busy}
          onChange={(next) => {
            setRequiresImei(next)

            if (next) {
              setRequiresSerial(true)
            }
          }}
          label="يتطلّب IMEI"
          hint="للهواتف — ويستلزم التسلسلَ معه."
        />

        <Toggle
          checked={requiresSerial}
          disabled={busy || requiresImei}
          onChange={setRequiresSerial}
          label="يتطلّب رقمَ تسلسل"
          hint={requiresImei ? 'مُلزَمٌ ما دام IMEI مطلوبًا.' : undefined}
        />

        <Toggle
          checked={isDefault}
          disabled={busy || variant.isDefault}
          onChange={setIsDefault}
          label="المتغيّرُ الافتراضيّ"
          hint={variant.isDefault ? 'هو الافتراضيُّ الآن — ويُغيَّر بترقية غيرِه.' : undefined}
        />
      </div>

      <ErrorBanner error={error} />

      <div className="flex gap-2">
        <Button tone="primary" disabled={busy} onClick={() => save.mutate()}>
          {busy ? 'يُحفظ…' : 'احفظ المتغيّر'}
        </Button>
        <Button onClick={onDone} disabled={busy}>إلغاء</Button>
      </div>
    </div>
  )
}

/** باركوداتُ المتغيّر: تُضاف وتُحذف — والحذفُ هنا حذفٌ حقيقيّ، ولا تاريخَ لرقم. */
export function BarcodeEditor({ variant, onChanged }: { variant: Variant; onChanged: () => void }) {
  const [value, setValue] = useState('')
  const [error, setError] = useState<unknown>(null)

  const add = useMutation({
    mutationFn: () =>
      api(`/api/v1/variants/${variant.id}/barcodes`, {
        method: 'POST',
        body: { barcode: value.trim(), isPrimary: variant.barcodes.length === 0 },
      }),
    onSuccess: () => {
      setValue('')
      setError(null)
      onChanged()
    },
    onError: setError,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/v1/barcodes/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setError(null)
      onChanged()
    },
    onError: setError,
  })

  // ترقيةُ رقمٍ ليصير الرئيسيّ. والإضافةُ لا ترقّي بنفسها عمدًا: رقمٌ ثانٍ
  // يُضاف ليُقرأ بالماسح، لا ليحلّ محلّ ما يُطبع على الملصق.
  const promote = useMutation({
    mutationFn: (id: string) => api(`/api/v1/barcodes/${id}/primary`, { method: 'POST' }),
    onSuccess: () => {
      setError(null)
      onChanged()
    },
    onError: setError,
  })

  const busy = add.isPending || remove.isPending || promote.isPending

  return (
    <div className="mt-3 space-y-2 rounded-md border border-line bg-surface p-3">
      <ul className="space-y-1">
        {variant.barcodes.map((barcode) => (
          <li key={barcode.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="tabular" dir="ltr">
              {barcode.barcode}
              {barcode.isPrimary && (
                <span className="mr-2 rounded bg-brand/10 px-1.5 py-0.5 text-xs text-brand">رئيسيّ</span>
              )}
            </span>
            <span className="flex gap-2">
              {!barcode.isPrimary && (
                <Button
                  onClick={() => promote.mutate(barcode.id)}
                  disabled={busy}
                  title="ما يُطبع على الملصق ويُعرض في القوائم"
                >
                  اجعله الرئيسيّ
                </Button>
              )}
              <Button onClick={() => remove.mutate(barcode.id)} disabled={busy}>احذف</Button>
            </span>
          </li>
        ))}
        {variant.barcodes.length === 0 && (
          <li className="text-xs text-ink-soft">لا باركودَ لهذا المتغيّر.</li>
        )}
      </ul>

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Field
            label="باركودٌ جديد"
            hint="امسحه بالماسح أو اكتبه. وللمادّة الواحدة أرقامٌ عدّة — علبةُ المصنع وملصقُ المحلّ مثلًا."
          >
            <TextInput value={value} onChange={setValue} dir="ltr" disabled={busy} />
          </Field>
        </div>
        <Button
          tone="primary"
          disabled={busy || value.trim() === ''}
          onClick={() => add.mutate()}
        >
          أضف
        </Button>
      </div>

      <ErrorBanner error={error} />
    </div>
  )
}

/** أرشفةٌ أو إعادةُ تفعيل — <b>ولا حذفَ لما له تاريخ</b>. */
export function ArchiveControl({
  title,
  path,
  isActive,
  reasonHint,
  onDone,
}: {
  title: string
  path: string
  isActive: boolean
  reasonHint: string
  onDone: () => void
}) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<unknown>(null)

  const run = useMutation({
    mutationFn: () =>
      api(path, { method: 'POST', body: { archived: isActive, reason: reason.trim() || null } }),
    onSuccess: () => {
      setError(null)
      setOpen(false)
      setReason('')
      onDone()
    },
    onError: setError,
  })

  if (!open) {
    return (
      <Button onClick={() => setOpen(true)}>{isActive ? title : 'أعِد التفعيل'}</Button>
    )
  }

  return (
    <div className="mt-2 w-full space-y-2 rounded-md border border-line bg-surface p-3">
      {isActive && (
        <Field label="السبب" hint={reasonHint}>
          <TextInput value={reason} onChange={setReason} disabled={run.isPending} />
        </Field>
      )}

      <ErrorBanner error={error} />

      <div className="flex gap-2">
        <Button
          tone="primary"
          disabled={run.isPending || (isActive && reason.trim() === '')}
          onClick={() => run.mutate()}
        >
          {run.isPending ? 'يُنفَّذ…' : isActive ? 'أرشِف' : 'أعِد التفعيل'}
        </Button>
        <Button onClick={() => setOpen(false)} disabled={run.isPending}>تراجع</Button>
      </div>
    </div>
  )
}

/** مفتاحٌ ثنائيّ — `parts.tsx` لا يحمل واحدًا، وهذه الشاشةُ تحتاج ثلاثة. */
function Toggle({
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  hint?: string
  disabled?: boolean
}) {
  return (
    <label className="flex items-start gap-2 text-sm">
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 accent-brand disabled:opacity-60"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>
        {label}
        {hint && <span className="block text-xs text-ink-soft">{hint}</span>}
      </span>
    </label>
  )
}
